import { z } from "zod";

const Hex32 = z.string().regex(/^0x[0-9a-fA-F]{64}$/u, "must be 0x-prefixed 32-byte hex");

const HexUint = z.string().regex(/^0x[0-9a-fA-F]+$/u, "must be 0x-prefixed hex");

const Sha256Hex = z.string().regex(/^[0-9a-fA-F]{64}$/u, "must be 64-char hex (no 0x prefix)");

const IsoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/u, "must be ISO 8601 UTC");

export const CkbfsRefSchema = z.object({
  tx_hash: Hex32,
  index: z.number().int().min(0),
  capacity: HexUint,
  type_id: Hex32,
});

export type CkbfsRef = z.infer<typeof CkbfsRefSchema>;

export const DocEntrySchema = z.object({
  id: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/u, "id must be kebab-case ascii"),
  title: z.string().min(1),
  cluster: z.string().min(1),
  slot: z.number().int().min(0),
  content_sha256: Sha256Hex,
  ckbfs: CkbfsRefSchema,
  version: z.number().int().min(1),
  source_url: z.string().url(),
  license: z.string().min(1),
  last_updated: IsoDate,
  // Per-doc image refs keyed by the markdown's literal image path (e.g.
  // "images/state-transitions.png" or "transaction-overview.png"). Each
  // entry is its own minted CKBFS cell; the renderer rewrites the
  // markdown's <img src=...> at display time.
  images: z.record(z.string().min(1), CkbfsRefSchema).optional(),
});

export type DocEntry = z.infer<typeof DocEntrySchema>;

export const ManifestSchema = z
  .object({
    version: z.literal(1),
    generated_at: IsoDate,
    docs: z.array(DocEntrySchema),
  })
  .superRefine((m, ctx) => {
    const seen = new Set<string>();
    for (const doc of m.docs) {
      if (seen.has(doc.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate doc id: ${doc.id}`,
          path: ["docs"],
        });
      }
      seen.add(doc.id);
    }
  });

export type Manifest = z.infer<typeof ManifestSchema>;
